import { createContext, useContext } from "react"

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Avatar } from "../Avatar"
import { cn } from "@/lib/utils"

// Shared chrome for the order-detail action dialogs. Owned here so a dialog
// can live in its own file (RequestRevisionDialog) without importing from the
// OrderModals registry that mounts it.

// Whether an order action is currently on the wire. Provided once around the
// active dialog rather than passed through every dialog's props, so a new
// dialog gets the in-flight behaviour by using Footer, without having to
// remember a prop. Defaults false so a Footer used outside the registry (or in
// a unit test) behaves as before.
const BusyContext = createContext(false)

export function BusyProvider({ busy, children }) {
  return <BusyContext.Provider value={busy}>{children}</BusyContext.Provider>
}

// For a dialog that renders its own footer instead of Footer: whether its
// action is on the wire, so it can disable its buttons the same way.
export function useBusy() {
  return useContext(BusyContext)
}

// Each modal is mounted only while active (so its form state resets every open);
// `open` is therefore always true and closing unmounts it.
export function Shell({ title, desc, onClose, children }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px]">{title}</DialogTitle>
          <DialogDescription className="sr-only">{desc || title}</DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  )
}

const NOTE_TONES = {
  red: "border-[color-mix(in_oklab,var(--primary)_25%,transparent)] bg-[var(--red-soft)]",
  info: "border-[color-mix(in_oklab,var(--needs-you)_25%,transparent)] bg-[color-mix(in_oklab,var(--needs-you)_9%,transparent)]",
  muted: "border-border bg-accent",
}

export function Note({ tone = "muted", children }) {
  return (
    <div className={cn("rounded-[10px] border px-[15px] py-[13px] text-[12.5px] leading-[1.55] text-muted-foreground", NOTE_TONES[tone])}>
      {children}
    </div>
  )
}

export function Body({ children }) {
  return <p className="text-[13.5px] leading-[1.6] text-muted-foreground">{children}</p>
}

export function Field({ label, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={htmlFor} className="text-xs font-bold text-foreground">
          {label}
        </label>
      )}
      {children}
    </div>
  )
}

export function WhoLine({ person, sub }) {
  return (
    <div className="flex items-center gap-[11px]">
      <Avatar initials={person.initials} name={person.name} size={42} />
      <div>
        <h4 className="text-sm font-bold">{person.name}</h4>
        <p className="text-[12px] text-ink-3">{sub}</p>
      </div>
    </div>
  )
}

export function Footer({ submitLabel, SubmitIcon, onSubmit, danger = false, cancelLabel = "Cancel", disabled = false }) {
  const busy = useContext(BusyContext)

  return (
    <DialogFooter>
      <DialogClose asChild>
        <Button variant="outline">{cancelLabel}</Button>
      </DialogClose>
      <Button
        className={cn("gap-[7px] font-bold", danger && "border-[color-mix(in_oklab,var(--primary)_45%,transparent)] bg-transparent text-primary hover:bg-[var(--red-soft)]")}
        variant={danger ? "outline" : "default"}
        onClick={onSubmit}
        disabled={disabled || busy}
      >
        {SubmitIcon && <SubmitIcon className="size-4" strokeWidth={2.1} />}
        {submitLabel}
      </Button>
    </DialogFooter>
  )
}

export function freelancerOf(order) {
  return order.team?.[0] || { initials: "★", name: "your freelancer", role: "Freelancer" }
}
